import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUser, FaEdit, FaCopy, FaCheck } from 'react-icons/fa';
import { showErrorToast, showSuccessToast } from '../../../shared/utils/toastHelper';
import apiClient from '../../../shared/utils/apiConfig';

const DoctorProfile = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const [profileData, setProfileData] = useState({
        name: '',
        description_ar: '',
        description_en: '',
        image: '',
        phone: '',
        code: ''
    });
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setTitle('الملف الشخصي');
        fetchProfileData();
    }, [setTitle]);

    const fetchProfileData = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get('/doctor/profile_data');
            
            if (response.data && response.data.status === 'success' && response.data.result) {
                const doctorData = response.data.result;
                
                // تعيين البيانات بالهيكل الصحيح
                setProfileData({
                    name: doctorData.name || '',
                    description_ar: doctorData.description || '', // الوصف يأتي في حقل description
                    description_en: doctorData.description_en || '',
                    image: doctorData.image || '',
                    phone: doctorData.phone || '',
                    code: doctorData.code || doctorData.invite_code || ''
                });
                
            } else {
                throw new Error(response.data?.message || 'فشل في جلب البيانات');
            }
        } catch (error) {
            showErrorToast(error.response?.data?.message || error.message || 'فشل في جلب البيانات');
        } finally {
            setLoading(false);
        }
    };


    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">جاري تحميل البيانات...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">
            <div className="dashboard-container">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">الملف الشخصي</h4>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-primary d-flex align-items-center"
                        onClick={() => navigate('/doctor/profile/edit')}
                        style={{ backgroundColor: '#184194', border: 'none' }}>
                        <FaEdit className="mx-2" />
                        تعديل الملف الشخصي
                    </motion.button>
                </div>

                {/* Profile Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="card shadow-lg border-0">
                    <div className="card-body p-4">
                        <div className="row">
                            {/* Profile Image */}
                            <div className="col-md-4 text-center mb-4">
                                <div className="position-relative d-inline-block">
                                    <div className="profile-image-container">
                                        {profileData.image ? (
                                            <img
                                                src={profileData.image}
                                                alt="Profile"
                                                className="profile-image"
                                            />
                                        ) : (
                                            <div className="profile-placeholder">
                                                <FaUser size={60} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Profile Details */}
                            <div className="col-md-8">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">الاسم</label>
                                        <p className="form-control-plaintext">{profileData.name || 'غير محدد'}</p>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">رقم الهاتف</label>
                                        <p className="form-control-plaintext">{profileData.phone || 'غير محدد'}</p>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">كود الدعوة</label>
                                        {profileData.code ? (
                                            <div className="input-group">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={profileData.code}
                                                    readOnly
                                                    style={{
                                                        background: '#f8f9fa',
                                                        border: '2px solid #e2e8f0',
                                                        fontWeight: '600',
                                                        fontSize: '1.1rem',
                                                        letterSpacing: '2px'
                                                    }}
                                                />
                                                <button
                                                    className="btn"
                                                    style={{
                                                        backgroundColor: '#184194',
                                                        color: 'white',
                                                        border: '2px solid #184194'
                                                    }}
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(profileData.code);
                                                        setCopied(true);
                                                        showSuccessToast('تم نسخ الكود بنجاح');
                                                        setTimeout(() => setCopied(false), 2000);
                                                    }}
                                                    title="نسخ الكود"
                                                >
                                                    {copied ? <FaCheck /> : <FaCopy />}
                                                </button>
                                            </div>
                                        ) : (
                                            <p className="form-control-plaintext text-muted">لا يوجد كود</p>
                                        )}
                                    </div>

                                    <div className="col-md-12 mb-3">
                                        <label className="form-label fw-bold">الوصف</label>
                                        <p className="form-control-plaintext">{profileData.description_ar || 'غير محدد'}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

        </div>
    );
};

export default DoctorProfile;

// CSS للتصميم
const style = document.createElement('style');
style.textContent = `
    .profile-image-container {
        width: 150px;
        height: 150px;
        border-radius: 50%;
        overflow: hidden;
        border: 4px solid #e9ecef;
        margin: 0 auto;
    }

    .profile-image {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .profile-placeholder {
        width: 100%;
        height: 100%;
        background: #f8f9fa;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #6c757d;
    }

    .form-control-plaintext {
        background: #f8f9fa;
        padding: 0.5rem;
        border-radius: 0.375rem;
        min-height: 38px;
        display: flex;
        align-items: center;
        margin: 0;
    }
`;
document.head.appendChild(style);
